import CollapseButton from '@orn/shared-web/components/Button/CollapseButton';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

interface IWidgetContextValue {
  /** Whether the body is currently collapsed (hidden). Only meaningful when `isCollapsible` is true. */
  isCollapsed: boolean;
  /** Toggle `isCollapsed`. Wired to the chevron button rendered by `Widget.Header`. */
  onToggle: () => void;
  /**
   * Whether this widget can be collapsed/expanded by the user.
   * When true, `Widget.Header` renders a `CollapseButton` and applies tighter
   * vertical padding to accommodate it.
   */
  isCollapsible: boolean;
  /**
   * Flat mode — renders the widget as a borderless section inside a parent card
   * instead of as a standalone card. Strips background, shadow, and rounding from
   * the outer container and applies denser header padding so nested groups don't
   * look like cards-within-cards.
   */
  flat: boolean;
}

const WidgetContext = React.createContext<IWidgetContextValue | null>(null);

interface IWidgetProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** Render as a flat section inside a parent card (no bg, shadow, or rounding). */
  flat?: boolean;
}

const Widget = ({
  children,
  className,
  collapsible = false,
  defaultCollapsed = false,
  flat = false,
  ...props
}: IWidgetProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);

  const onToggle = React.useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const contextValue = React.useMemo<IWidgetContextValue>(
    () => ({
      isCollapsed,
      onToggle,
      isCollapsible: collapsible,
      flat,
    }),
    [isCollapsed, onToggle, collapsible, flat]
  );

  return (
    <WidgetContext.Provider value={collapsible || flat ? contextValue : null}>
      <div
        className={cx(
          'flex flex-col',
          !flat && 'bg-secondary_subtle border-secondary shadow-card overflow-hidden rounded-2xl border',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </WidgetContext.Provider>
  );
};

interface IWidgetHeaderProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
}

const WidgetHeader = ({ title, subtitle, trailing, className, ...props }: IWidgetHeaderProps) => {
  const context = React.useContext(WidgetContext);
  const isCollapsible = context?.isCollapsible ?? false;
  const isEmbedded = context?.flat ?? false;

  return (
    <div
      className={cx(
        'border-tertiary flex items-center justify-between border-b',
        isEmbedded ? 'py-3 pl-5 pr-4' : cx('bg-secondary p-5', isCollapsible && 'py-4.25 px-5'),
        className
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
        {/* `max-w-full`: under `items-start` the box is content-sized, so without it `truncate` never engages. */}
        <div className="text-primary max-w-full truncate text-lg font-semibold">{title}</div>
        {subtitle && <div className="text-tertiary max-w-full truncate text-sm font-normal">{subtitle}</div>}
      </div>
      <div className={cx('flex shrink-0 items-center justify-center self-center', isEmbedded ? 'gap-0.5' : 'gap-2')}>
        {trailing}
        {isCollapsible && context && (
          <CollapseButton isCollapsed={context.isCollapsed} onToggle={() => context.onToggle()} />
        )}
      </div>
    </div>
  );
};

interface IWidgetBodyProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
}

const WidgetBody = ({ children, className, ...props }: IWidgetBodyProps) => {
  const context = React.useContext(WidgetContext);
  const isCollapsed = context?.isCollapsed ?? false;

  if (isCollapsed) {
    return null;
  }

  return (
    <div className={cx('flex flex-col', className)} {...props}>
      {children}
    </div>
  );
};

interface IWidgetRowProps extends React.ComponentPropsWithoutRef<'div'> {
  label: React.ReactNode;
  value?: React.ReactNode;
  valueClassName?: string;
  /** Default variant only. */
  icon?: React.ReactNode;
  /** Default variant only. */
  subline?: React.ReactNode;
  /**
   * `summary` is the Summary card row: a 15px medium label in the tertiary tone and 8px of vertical
   * padding. The label and value share a line when they fit; otherwise the value goes on the next
   * line, and either one truncates only when it is wider than the row by itself.
   */
  variant?: 'default' | 'summary';
}

const WidgetRow = ({
  label,
  value,
  valueClassName,
  icon,
  subline,
  variant = 'default',
  className,
  ...props
}: IWidgetRowProps) => {
  const labelId = React.useId();
  const isSummary = variant === 'summary';

  const valueElement = value != null && (
    <span
      aria-labelledby={labelId}
      className={cx(
        'text-md font-medium',
        isSummary ? 'max-w-full truncate leading-[22px]' : 'shrink-0',
        valueClassName || 'text-primary'
      )}
    >
      {value}
    </span>
  );

  if (isSummary) {
    return (
      <div className={cx('flex flex-wrap items-center justify-between gap-x-4 px-5 py-2', className)} {...props}>
        <div id={labelId} className="text-tertiary text-reg max-w-full truncate font-medium">
          {label}
        </div>
        {valueElement}
      </div>
    );
  }

  return (
    <div className={cx('flex items-center justify-between gap-5 px-5 py-3', className)} {...props}>
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        {icon && <div className="shrink-0">{icon}</div>}
        <div id={labelId} className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {label}
          {subline && <div className="text-tertiary truncate text-sm font-normal">{subline}</div>}
        </div>
      </div>
      {valueElement}
    </div>
  );
};

Widget.Header = WidgetHeader;
Widget.Body = WidgetBody;
Widget.Row = WidgetRow;

export default Widget;
